import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {Slot, TicketConfiguration} from '../../../../Models';
import TicketPrice from '../TicketPrice/TicketPrice.vue';
import TicketDescription from '../TicketDescription/TicketDescription.vue';
import IntoCart from '../../../Icons/IntoCart.vue';
import Check from '../../../Icons/Check.vue';
import Arrow from '../../../Icons/Arrow.vue';
import Modal from '../../../Modal/Modal.vue';
import {ProductQuantityUtility} from '../../../../Utilities';
import {TicketConfigurationMixin} from '../../../../Mixins/TicketConfigurationMixin';

@Component({
  components: {
    IntoCart,
    Check,
    Arrow,
    Modal,
    TicketPrice,
    TicketDescription,
  },
})
export default class TicketConfigurationItem extends TicketConfigurationMixin {
  @Prop({type: TicketConfiguration, required: true}) ticketConfiguration: TicketConfiguration | undefined;
  @Prop({type: Boolean, default: () => false}) loading: boolean | undefined;

  @Watch('ticketConfiguration.quantity')
  onQuantityChanged(newVal: number, oldVal: number) {
    if (newVal && this.ticketConfiguration) {
      const isProdFamily = this.ticketConfiguration.subTicketConfigurations.length > 0;
      if (
        this.ticketConfiguration.product.maxOrderQuantity
        && newVal > this.ticketConfiguration.product.maxOrderQuantity
      ) {
        this.ticketConfiguration.quantity = isProdFamily ? 1 : this.ticketConfiguration.product.maxOrderQuantity;
      }
      if (newVal < 0) {
        this.ticketConfiguration.quantity = 0;
      }

      if (newVal > 1 && isProdFamily) {
        this.ticketConfiguration.quantity = 1;
      }
    }
  }

  private addToCart(ticketConfiguration: TicketConfiguration): void {
    this.$emit('addToCart', ticketConfiguration);
  }

  get smallestQuotaSlot(): Slot | null {
    if (this.slotsProp && this.ticketConfiguration && this.ticketConfiguration!.product.slots.length) {
      return this.ticketConfiguration!.product.slots
        .map(sId => this.slotsProp!.find(s => s.id === sId))
        .filter(s => s !== undefined)
        .reduce(
          function (prev: Slot | null | undefined, curr: Slot | null | undefined) {
            return prev !== undefined && curr !== undefined && prev !== null && curr !== null && prev.quota < curr.quota ? prev : curr;
          },
          null,
        ) || null;
    }
    return null;
  }

  get isSoldOut() {
    if (this.slotsProp && this.ticketConfiguration) {
      return this.ticketConfiguration!.product.slots.find(sId => {
        const slot = this.slotsProp!.find(s => s.id === sId);
        return slot && slot.quota === 0;
      }) !== undefined;
    }
    return false;
  }

  get disableDecrease(): boolean {
    if (this.ticketConfiguration) {
      return this.ticketConfiguration.quantity === 0;
    }
    return true;
  }

  get disableIncrease(): boolean {
    if (this.ticketConfiguration) {
      return (this.ticketConfiguration.subTicketConfigurations.length > 0 && this.ticketConfiguration.quantity === 1)
        || !this.productPurchasable
        || this.reachedMaxPositionQuantity
        || this.reachedMaxOrderQuantity
        || this.isSoldOut
        || this.reachedQuotaLimit
        || !this.additionalProductPurchasable;
    }
    return true;
  }

  get productPurchasable(): boolean {
    if (this.ticketConfiguration) {
      const purchasableFrom = new Date(this.ticketConfiguration.product.bookingPosStart);
      const purchasableUntil = new Date(this.ticketConfiguration.product.bookingPosEnd);
      const now = new Date();
      return (purchasableFrom <= now && purchasableUntil >= now);
    }
    return false;
  }

  get additionalProductPurchasable(): boolean {
    let purchasable = true;
    if (this.ticketConfiguration && this.ticketConfiguration.isAdditionalProduct) {
      purchasable = false;
      if (this.cart === null) {
        return false;
      }

      const eventId = this.ticketConfiguration.event.id;

      this.cart.order.orderItems.forEach((oi) => {
        const product = this.findProduct(oi.productId);
        if (product && product.price > 0 && product.eventId === eventId) {
          purchasable = true;
        }
      });
    }
    return purchasable;
  }

  get reachedMaxPositionQuantity(): boolean {
    if (this.ticketConfiguration) {
      const product = this.ticketConfiguration.product;
      return (new ProductQuantityUtility(this.currentOrder))
        .reachedMaxPositionQuantity(product, this.ticketConfiguration.quantity);
    }
    return false;
  }

  get reachedMinPositionQuantity() {
    if (this.ticketConfiguration) {
      const product = this.ticketConfiguration.product;
      return (new ProductQuantityUtility(this.currentOrder))
        .reachedMinPositionQuantity(product, this.ticketConfiguration.quantity);
    }
    return false;
  }

  get reachedMaxOrderQuantity(): boolean {
    if (this.ticketConfiguration) {
      const product = this.ticketConfiguration.product;
      return (new ProductQuantityUtility(this.currentOrder))
        .reachedMaxOrderQuantity(product, this.ticketConfiguration.quantity);
    }
    return false;
  }

  get reachedQuotaLimit(): boolean {
    if (this.ticketConfiguration && this.smallestQuotaSlot) {
      return this.ticketConfiguration.quantity >= this.smallestQuotaSlot.quota;
    }
    return false;
  }

  get currentTotalOfProduct() {
    let currentTotalOfProduct = 0;
    if (this.ticketConfiguration) {
      // all have the same product
      const product = this.ticketConfiguration.product;
      currentTotalOfProduct = (new ProductQuantityUtility(this.currentOrder))
        .currentTotalOfProduct(product);
    }
    return currentTotalOfProduct;
  }
}
